import { getModel } from '@smx-ui-model/store-model';
import { GenericModelSchemaType, StoreModelType, TableResourceType } from '@smx-ui-model/types';
import { Alert, ConfigProvider, Divider, Flex, Form, Spin } from 'antd';
import * as React from 'react';
import { memo, useContext } from 'react';

import { FieldNumber } from './field/field-number';
import { FieldString } from './field/field-string';
import FormProcessor from './form-processor';
import { SmxInspectorContext } from './inspector-provider';
import LoaderDefaultDataFormInspector from './loader-default-data-form-inspector';
import LoaderFormInspector from './loader-form-inspector';
import LoaderParentDataFormInspector from './loader-parent-data-form-inspector';

interface FormInspectorProps {
    modelName: keyof StoreModelType;
    entity?: TableResourceType;
}

const FormInspector: React.FC<FormInspectorProps> = ({ modelName, entity }) => {
    const [form] = Form.useForm();
    const { isLoading, currentId, parentNode, parentModelName, filterProject } = useContext(SmxInspectorContext);
    const model = getModel<GenericModelSchemaType>(modelName);

    const currentContext = entity
        ? model.context?.inspectorInputEdit || model.context?.inspectorInput
        : model.context?.inspectorInput;

    const createField = (fieldName: string) => {
        if (!currentId && !currentContext?.some((subFieldName) => fieldName === subFieldName)) {
            return null;
        }

        const renderFromModel = (model.renderData?.inspectorField as any)?.[fieldName];
        const field = (model.schema.field as any)[fieldName];

        if (renderFromModel) {
            return (
                <div key={fieldName}>
                    {renderFromModel({ form, schemaField: field, dataField: entity, parentNode, filterProject })}
                </div>
            );
        }

        switch (field.type) {
            case 'string':
                return <FieldString key={fieldName} field={field} entity={entity!} />;
            case 'integer':
                return <FieldNumber key={fieldName} field={field} />;
            default:
                return <Alert key={fieldName} message={`Error field type - ${field.type}`} type="error" showIcon />;
        }
    };

    return (
        <ConfigProvider
            theme={{
                components: {
                    Form: {
                        itemMarginBottom: 10,
                        verticalLabelPadding: 0,
                    },
                },
            }}
        >
            <Flex vertical flex={1} style={{ overflowY: 'auto' }}>
                <Spin spinning={isLoading}>
                    <Form name={modelName + entity?.key} layout="vertical" form={form} autoComplete="off">
                        {!entity && <LoaderParentDataFormInspector parentModelName={parentModelName} />}
                        {!entity && <LoaderDefaultDataFormInspector modelName={modelName} />}
                        {entity && <LoaderFormInspector entity={entity} modelName={modelName} />}
                        {currentContext?.map(createField)}
                    </Form>
                </Spin>
            </Flex>
            <Divider style={{ margin: '5px 0' }} />
            <Flex style={{ marginTop: 5 }}>
                <FormProcessor modelName={modelName} form={form} entity={entity} />
            </Flex>
        </ConfigProvider>
    );
};

export default memo(FormInspector);
